/**
 * This action type will be dispatched by the history actions below.
 * If you're writing a middleware to watch for navigation events, be sure to
 * look for actions of this type.
 */
export const CALL_HISTORY_METHOD = '@@router/CALL_HISTORY_METHOD';

/**
 * This action type will be dispatched when your history
 * receives a location change.
 */
export const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';

const getIn = (state, path) => {
    if (!state) {
        return state;
    }

    const { length } = path;

    if (!length) {
        return undefined;
    }

    let result = state;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < length && !!result; ++i) {
        result = result[path[i]];
    }

    return result;
};

/**
 * Adds query to location.
 * Utilises the search prop of location to construct query.
 */
const injectQuery = (location) => {
    if (location && location.query) {
        // Don't inject query if it already exists in history
        return location;
    }

    const searchQuery = location && location.search;

    if (typeof searchQuery !== 'string' || searchQuery.length === 0) {
        return {
            ...location,
            query: {},
        };
    }

    // Ignore the `?` part of the search string e.g. ?username=codejockie
    const search = searchQuery.substring(1);
    // Split the query string on `&` e.g. ?username=codejockie&name=Kennedy
    const queries = search.split('&');
    // Cosntruct query
    const query = queries.reduce((acc, currentQuery) => {
        // Split on `=`, to get key and value
        const [queryKey, queryValue] = currentQuery.split('=');
        return {
            ...acc,
            [queryKey]: queryValue,
        };
    }, {});

    return {
        ...location,
        query,
    };
};

const createConnectRouter = (structure) => {
    const {
        fromJS,
        merge,
    } = structure;

    return (history) => {
        const initialRouterState = fromJS({
            location: injectQuery(history.location),
            action: history.action,
        });

        /*
        * This reducer will update the state with the most recent location history
        * has transitioned to.
        */
        return (state = initialRouterState, { type, payload } = {}) => {
            if (type === LOCATION_CHANGE) {
                const { location, action, isFirstRendering } = payload;
                // Don't update the state ref for the first rendering
                // to prevent the double-rendering issue on initilization
                return isFirstRendering
                    ? state
                    : merge(state, { location: fromJS(injectQuery(location)), action });
            }

            return state;
        };
    };
};

export const connectRouter = createConnectRouter({
    fromJS: value => value,
    getIn,
    merge: (state, payload) => ({ ...state, ...payload }),
    toJS: value => value,
});

/**
 * This middleware captures CALL_HISTORY_METHOD actions to redirect to the
 * provided history object. This will prevent these actions from reaching your
 * reducer or any middleware that comes after this one.
 */
// eslint-disable-next-line consistent-return
export const routerMiddleware = h => s => n => (a) => { // eslint-disable-line no-unused-vars
    if (a.type !== CALL_HISTORY_METHOD) {
        return n(a);
    }

    const { payload: { method, args } } = a;
    h[method](...args);
};
